import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslations } from '../utils/translations';
import './ContentSlide.css';

const ContentSlide = ({ slide, currentLanguage }) => {
  const { t } = useTranslations(currentLanguage);
  const videoRef = useRef(null);
  const slideContainerRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [videoPosition, setVideoPosition] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  // Track if video position is stable (initial positioning complete)
  const [isPositionStable, setIsPositionStable] = useState(false);
  // Track if we're in a critical update (bar visibility change, initial load)
  const [isCriticalUpdate, setIsCriticalUpdate] = useState(true);
  // Track video playback state to preserve it during position updates
  const videoStateRef = useRef({ currentTime: 0, wasPlaying: false });
  // Track if video has been initialized to prevent restart on re-renders
  const videoInitializedRef = useRef(false);
  // Debounce resize/position updates to prevent excessive video reloads
  const positionUpdateTimeoutRef = useRef(null);
  // Track if image is currently loading to prevent premature error handling
  const imageLoadingRef = useRef(false);
  const imageErrorTimeoutRef = useRef(null);
  
  // Detect iOS devices
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                       (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
    console.log('ContentSlide: iOS device detected:', isIOSDevice);
  }, []);
  
  // Debug: Log slide data structure
  useEffect(() => {
    console.log('ContentSlide: Complete slide data:', {
      slideNumber: slide.SlideNumber || slide.slideNumber,
      videos: slide.Videos || slide.videos,
      videoMargins: slide.VideoMargins || slide.videoMargins,
      currentLanguage,
      hasVideos: !!(slide.Videos || slide.videos),
      hasVideoMargins: !!(slide.VideoMargins || slide.videoMargins),
      isIOS
    });
    
    // Detailed video margin structure analysis
    const videoMargins = slide.VideoMargins || slide.videoMargins;
    if (videoMargins) {
      console.log('ContentSlide: VideoMargins structure analysis:', {
        availableLanguages: Object.keys(videoMargins),
        enMargins: videoMargins.en,
        enMarginsKeys: videoMargins.en ? Object.keys(videoMargins.en) : 'N/A'
      });
    }
  }, [slide, currentLanguage, isIOS]);
  
  // Get video files for current language
  const getVideoFiles = useCallback(() => {
    const langCode = currentLanguage || 'en';
    const langCodeUpper = langCode.charAt(0).toUpperCase() + langCode.slice(1);
    
    console.log('ContentSlide: Getting video for language:', langCode, 'on slide:', slide.SlideNumber || slide.slideNumber);
    console.log('ContentSlide: Full slide data:', slide);
    
    // Try both uppercase and lowercase property names
    const videos = slide.Videos || slide.videos;
    
    console.log('ContentSlide: Available videos object:', videos);
    console.log('ContentSlide: Videos object keys:', videos ? Object.keys(videos) : 'No videos object');
    
    if (videos) {
      // Try both language code formats
      const videoUrl = videos[langCode] || videos[langCodeUpper] || videos['en'] || videos['En'] || slide.videoFiles;
      console.log('ContentSlide: Selected video URL for', langCode + ':', videoUrl);
      console.log('ContentSlide: Video URL type:', typeof videoUrl);
      console.log('ContentSlide: Video URL length:', videoUrl ? videoUrl.length : 'null');
      
      // Log video URL without making any network requests to avoid CORS issues
      if (videoUrl) {
        console.log('ContentSlide: Video URL selected:', videoUrl);
        // Only log domain if URL is valid, without making network requests
        try {
          console.log('ContentSlide: Video URL domain:', new URL(videoUrl).hostname);
        } catch (e) {
          console.log('ContentSlide: Video URL format check failed, but continuing');
        }
      }
      
      return videoUrl;
    }
    
    console.log('ContentSlide: No videos available, using slide.videoFiles:', slide.videoFiles);
    return slide.videoFiles || null;
  }, [slide, currentLanguage]);

  // Validate if video should be shown (both video file and margins must exist)
  const shouldShowVideo = useCallback(() => {
    console.log('ContentSlide: shouldShowVideo called for language:', currentLanguage);
    
    const videoSrc = getVideoFiles();
    console.log('ContentSlide: Video source check:', {
      videoSrc,
      hasVideoSrc: !!videoSrc
    });
    
    if (!videoSrc) {
      console.log('❌ ContentSlide: No video source found');
      return false;
    }

    // Try both lowercase and capitalized language keys to handle API variations
    const langCode = currentLanguage || 'en';
    const langCodeCapitalized = langCode.charAt(0).toUpperCase() + langCode.slice(1);
    
    // Try both formats: lowercase (en, ar, ur, ma) and capitalized (En, Ar, Ur, Ma)
    const margins = slide.VideoMargins?.[langCode] || slide.VideoMargins?.[langCodeCapitalized];
    
    console.log('ContentSlide: Margin check:', {
      langCode,
      langCodeCapitalized,
      availableMarginKeys: Object.keys(slide.VideoMargins || {}),
      foundMargins: !!margins,
      marginData: margins
    });
    
    if (!margins) {
      console.log('❌ ContentSlide: No margins found for language:', currentLanguage);
      return false;
    }
    
    // Check if we have valid margin data using EXACT API property names
    // ALL values must exist AND not be empty strings AND be valid numbers
    const hasValidMargins = margins.xPercentage && 
      margins.yPercentage && 
      margins.widthPercentage && 
      margins.heightPercentage &&
      margins.xPercentage !== "" && 
      margins.yPercentage !== "" && 
      margins.widthPercentage !== "" && 
      margins.heightPercentage !== "" &&
      margins.xPercentage.trim() !== "" && 
      margins.yPercentage.trim() !== "" && 
      margins.widthPercentage.trim() !== "" && 
      margins.heightPercentage.trim() !== "" &&
      !isNaN(parseFloat(margins.xPercentage)) &&
      !isNaN(parseFloat(margins.yPercentage)) &&
      !isNaN(parseFloat(margins.widthPercentage)) &&
      !isNaN(parseFloat(margins.heightPercentage));
    
    console.log('ContentSlide: shouldShowVideo final check:', {
      videoSrc,
      langCode,
      hasValidMargins,
      marginProperties: {
        xPercentage: `"${margins.xPercentage}"`,
        yPercentage: `"${margins.yPercentage}"`,
        widthPercentage: `"${margins.widthPercentage}"`,
        heightPercentage: `"${margins.heightPercentage}"`,
        xPercentageValid: margins.xPercentage && margins.xPercentage !== "" && margins.xPercentage.trim() !== "" && !isNaN(parseFloat(margins.xPercentage)),
        yPercentageValid: margins.yPercentage && margins.yPercentage !== "" && margins.yPercentage.trim() !== "" && !isNaN(parseFloat(margins.yPercentage)),
        widthPercentageValid: margins.widthPercentage && margins.widthPercentage !== "" && margins.widthPercentage.trim() !== "" && !isNaN(parseFloat(margins.widthPercentage)),
        heightPercentageValid: margins.heightPercentage && margins.heightPercentage !== "" && margins.heightPercentage.trim() !== "" && !isNaN(parseFloat(margins.heightPercentage)),
        allKeys: Object.keys(margins)
      }
    });
    
    // Show video ONLY if we have video source AND valid margins (all parameters must have valid values)
    const shouldShow = !!videoSrc && hasValidMargins;
    
    if (shouldShow) {
      console.log('ContentSlide: ✅ shouldShowVideo: Valid margins found, showing video');
    } else {
      console.log('ContentSlide: ❌ shouldShowVideo: Invalid or empty margins, NOT showing video');
    }
    
    console.log('ContentSlide: shouldShowVideo result:', shouldShow);
    
    return shouldShow;
  }, [slide, currentLanguage, getVideoFiles]);

  // Reset image when slide changes - ensure image loads properly after navigation
  useEffect(() => {
    const slideNumber = slide.slideNumber || slide.SlideNumber;
    console.log('ContentSlide: Slide changed, resetting image for slide:', slideNumber);
    
    // Reset image loaded state
    setImageLoaded(false);
    
    // Reset image element when slide changes to ensure proper loading
    if (imageRef.current) {
      // Reset image display and ensure it's visible
      imageRef.current.style.display = '';
      
      // Hide fallback content immediately
      const fallbackDiv = imageRef.current.parentElement?.querySelector('.slide-fallback-content');
      if (fallbackDiv) {
        fallbackDiv.style.display = 'none';
      }
      
      // Get the new image source
      const newImageSrc = getSlideImage();
      if (newImageSrc) {
        // Clear any pending error timeout
        if (imageErrorTimeoutRef.current) {
          clearTimeout(imageErrorTimeoutRef.current);
          imageErrorTimeoutRef.current = null;
        }
        
        // Set loading flag
        imageLoadingRef.current = true;
        
        // Remove error listeners temporarily to prevent false errors during reset
        const currentSrc = imageRef.current.src;
        
        // Only reload if the src is actually different
        if (currentSrc !== newImageSrc) {
          // Set new src directly - browser will handle reload
          imageRef.current.src = newImageSrc;
          console.log('ContentSlide: Setting new image src for slide:', slideNumber, 'URL:', newImageSrc);
        } else {
          // Same src, just ensure it's visible
          console.log('ContentSlide: Image src unchanged for slide:', slideNumber);
          imageLoadingRef.current = false; // Not loading if src is same
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide.slideNumber, slide.SlideNumber, currentLanguage]); // Re-run when slide number or language changes

  // Effect to handle video autoplay and looping when slide changes
  useEffect(() => {
    // Don't reset videoPosition here - let position calculation happen immediately
    // This prevents the black border flash and delay before video appears
    videoInitializedRef.current = false; // Reset initialization flag on slide/language change
    
    const videoSrc = getVideoFiles();
    console.log('ContentSlide: useEffect - Video source:', videoSrc);
    console.log('ContentSlide: useEffect - Video ref exists:', !!videoRef.current);
    
    if (videoRef.current && videoSrc) {
      console.log('ContentSlide: Setting up video without CORS restrictions:', videoSrc);
      
      // Only reset video if src has changed (not on resize/position updates)
      const currentSrc = videoRef.current.src || videoRef.current.currentSrc;
      const videoSrcUrl = typeof videoSrc === 'string' ? videoSrc : (videoSrc?.[0] || '');
      
      if (currentSrc !== videoSrcUrl && videoSrcUrl) {
        console.log('ContentSlide: Video source changed, updating video');
      // Pause current video
      videoRef.current.pause();
      
      // Set basic video properties for maximum compatibility
      videoRef.current.muted = true;
      videoRef.current.loop = true;
      videoRef.current.playsInline = true;
      videoRef.current.preload = 'metadata'; // Load metadata only to avoid CORS issues
      // Remove crossorigin attribute to avoid CORS preflight requests
      videoRef.current.removeAttribute('crossorigin');
      
      // Set src directly on video element (this will trigger loading automatically)
        videoRef.current.src = videoSrcUrl;
      } else if (currentSrc === videoSrcUrl) {
        // Same source, just ensure properties are set (don't reload)
        console.log('ContentSlide: Video source unchanged, skipping reload');
        videoRef.current.muted = true;
        videoRef.current.loop = true;
        videoRef.current.playsInline = true;
        // Don't call load() - video is already loaded
      }
      
      // Add event listeners for debugging
      const video = videoRef.current;
      
      const handleLoadStart = () => console.log('ContentSlide: Video load started');
      const handleLoadedData = () => console.log('ContentSlide: Video data loaded');
      const handleCanPlay = () => console.log('ContentSlide: Video can play');
      const handleError = (e) => {
        console.warn('ContentSlide: Video loading issue (this is normal for cross-origin videos):', {
          errorCode: e.target.error?.code,
          errorMessage: e.target.error?.message,
          videoSrc: e.target.src,
          readyState: e.target.readyState,
          networkState: e.target.networkState
        });
        
        // For CORS errors (network state 3), try to continue anyway
        if (e.target.networkState === 3) {
          console.log('ContentSlide: Network error detected, but video may still work');
          // Don't prevent video from trying to play
        }
      };
      
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);
      
      // Aggressive autoplay strategy - force video to play without user interaction
      // IMPORTANT: Only run if video hasn't been initialized (prevents restart on resize)
      const forceAutoplay = async () => {
        if (!videoRef.current) return;
        
        // If video is already initialized and playing, don't restart
        if (videoInitializedRef.current && videoRef.current.readyState >= 2) {
          console.log('ContentSlide: Video already initialized, skipping autoplay');
          return;
        }
        
        const video = videoRef.current;
        console.log('ContentSlide: Force autoplay attempt, readyState:', video.readyState, 'isIOS:', isIOS);
        
        // Configure video for maximum compatibility
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('x-webkit-airplay', 'allow');
        video.volume = 0; // Ensure volume is 0
        
        // Multiple autoplay attempts with different strategies
        const playStrategies = [
          // Strategy 1: Direct play
          () => video.play(),
          
          // Strategy 2: Wait for ready state and play
          () => {
            if (video.readyState < 2) {
            return new Promise(resolve => {
              video.addEventListener('loadeddata', () => resolve(video.play()), { once: true });
            });
            }
            return video.play();
          },
          
          // Strategy 3: Play without resetting (don't reset currentTime if video is already playing)
          () => {
            // Only reset if video hasn't started playing yet
            if (video.currentTime === 0 && video.paused) {
              return video.play();
            }
            // If video is paused but has currentTime, just resume
            if (video.paused) {
            return video.play();
            }
            return Promise.resolve();
          }
        ];
        
        for (let i = 0; i < playStrategies.length; i++) {
          try {
            console.log(`ContentSlide: Trying autoplay strategy ${i + 1}`);
            await playStrategies[i]();
            console.log(`ContentSlide: Autoplay successful with strategy ${i + 1}`);
            videoInitializedRef.current = true; // Mark as initialized
            return; // Success, exit
          } catch (error) {
            console.log(`ContentSlide: Strategy ${i + 1} failed:`, error.message);
            if (i === playStrategies.length - 1) {
              console.log('ContentSlide: All autoplay strategies failed, but continuing without play button');
              // Don't show play button - just continue
            }
          }
        }
      };
      
      // Try with progressive delays to avoid overwhelming the server
      const timer1 = setTimeout(forceAutoplay, 200);
      const timer2 = setTimeout(forceAutoplay, 800);
      const timer3 = setTimeout(forceAutoplay, 1500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        if (video) {
          video.removeEventListener('loadstart', handleLoadStart);
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('canplay', handleCanPlay);
          video.removeEventListener('error', handleError);
        }
      };
    } else {
      console.log('ContentSlide: No video setup - videoRef:', !!videoRef.current, 'videoSrc:', videoSrc);
    }
  }, [slide.SlideNumber, currentLanguage, getVideoFiles, isIOS]); // Re-run when slide or language changes

  // Intersection Observer to force autoplay when video comes into view
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            console.log('ContentSlide: Video in view, forcing autoplay');
            
            // Configure for maximum compatibility
            video.muted = true;
            video.volume = 0;
            video.playsInline = true;
            video.setAttribute('playsinline', 'true');
            video.setAttribute('webkit-playsinline', 'true');
            
            // Multiple aggressive play attempts
            const forcePlayOnView = async () => {
              for (let i = 0; i < 5; i++) {
                try {
                  await video.play();
                  console.log(`ContentSlide: Intersection autoplay successful on attempt ${i + 1}`);
                  break;
                } catch (error) {
                  console.log(`ContentSlide: Intersection autoplay attempt ${i + 1} failed:`, error.message);
                  if (i < 4) {
                    await new Promise(resolve => setTimeout(resolve, 200 * (i + 1)));
                  }
                }
              }
            };
            
            forcePlayOnView();
          }
        });
      },
      { threshold: [0.5] }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [videoPosition]); // Re-run when video position changes

  // Calculate video position using direct percentage values from VideoMargins
  const calculateVideoPosition = useCallback(() => {
    console.log('ContentSlide: calculateVideoPosition called for language:', currentLanguage);
    
    if (!slide.VideoMargins) {
      console.log('❌ ContentSlide: No VideoMargins in slide data');
      return null;
    }

    // Try both lowercase and capitalized language keys to handle API variations
    const langCode = currentLanguage || 'en';
    const langCodeCapitalized = langCode.charAt(0).toUpperCase() + langCode.slice(1);
    
    // Try both formats: lowercase (en, ar, ur, ma) and capitalized (En, Ar, Ur, Ma)
    const margins = slide.VideoMargins[langCode] || slide.VideoMargins[langCodeCapitalized];

    console.log('ContentSlide: Video margin calculation:', {
      langCode,
      langCodeCapitalized,
      availableMarginKeys: Object.keys(slide.VideoMargins || {}),
      foundMargins: margins,
      imageLoaded,
      imageRefExists: !!imageRef.current
    });

    if (!margins) {
      console.log('❌ ContentSlide: No margins found for language:', currentLanguage);
      return null;
    }

    // If image not loaded yet, calculate position using container dimensions IMMEDIATELY
    // This ensures video appears at the same time as slide renders (no delay)
    if (!imageLoaded || !imageRef.current) {
      console.log('⚠️ ContentSlide: Image not loaded yet, calculating IMMEDIATE fallback positioning');
      
      // Try to get container dimensions from slideContainerRef
      const container = slideContainerRef.current?.querySelector('.slide-image-container');
      if (container && margins) {
        const containerRect = container.getBoundingClientRect();
        if (containerRect.width > 0 && containerRect.height > 0) {
          // Calculate approximate image area using aspect ratio
          const containerAspectRatio = containerRect.width / containerRect.height;
          const imageAspectRatio = 3000 / 1688; // Slide aspect ratio
          
          let imageAreaLeft = 0;
          let imageAreaTop = 0;
          let imageAreaWidth = 100;
          let imageAreaHeight = 100;
          
          if (imageAspectRatio > containerAspectRatio) {
            const displayedHeight = containerRect.width / imageAspectRatio;
            const verticalOffset = (containerRect.height - displayedHeight) / 2;
            imageAreaTop = (verticalOffset / containerRect.height) * 100;
            imageAreaHeight = (displayedHeight / containerRect.height) * 100;
          } else {
            const displayedWidth = containerRect.height * imageAspectRatio;
            const horizontalOffset = (containerRect.width - displayedWidth) / 2;
            imageAreaLeft = (horizontalOffset / containerRect.width) * 100;
            imageAreaWidth = (displayedWidth / containerRect.width) * 100;
          }
          
          // Use margins to calculate position immediately
          const videoXPercent = parseFloat(margins.xPercentage || 0);
          const videoYPercent = parseFloat(margins.yPercentage || 0);
          const videoWidthPercent = parseFloat(margins.widthPercentage || 0);
          const videoHeightPercent = parseFloat(margins.heightPercentage || 0);
          
          const adjustedX = imageAreaLeft + (videoXPercent * imageAreaWidth / 100);
          const adjustedY = imageAreaTop + (videoYPercent * imageAreaHeight / 100);
          const adjustedWidth = (videoWidthPercent * imageAreaWidth / 100);
          const adjustedHeight = (videoHeightPercent * imageAreaHeight / 100);
          
          console.log('ContentSlide: Using IMMEDIATE fallback position from container:', {
            adjustedX, adjustedY, adjustedWidth, adjustedHeight
          });
          
          return {
            position: 'absolute',
            left: `${adjustedX}%`,
            top: `${adjustedY}%`,
            width: `${adjustedWidth}%`,
            height: `${adjustedHeight}%`,
            objectFit: 'cover',
            zIndex: 3,
            borderRadius: '0px',
            overflow: 'hidden'
          };
        }
      }
      
      // If container not ready, use margin percentages directly as fallback
      // This ensures video always has a position immediately
      const videoXPercent = parseFloat(margins.xPercentage || 0);
      const videoYPercent = parseFloat(margins.yPercentage || 0);
      const videoWidthPercent = parseFloat(margins.widthPercentage || 0);
      const videoHeightPercent = parseFloat(margins.heightPercentage || 0);
      
      console.log('ContentSlide: Using IMMEDIATE default position (container not ready):', {
        videoXPercent, videoYPercent, videoWidthPercent, videoHeightPercent
      });
      
      return {
        position: 'absolute',
        left: `${videoXPercent}%`,
        top: `${videoYPercent}%`,
        width: `${videoWidthPercent}%`,
        height: `${videoHeightPercent}%`,
        objectFit: 'cover',
        zIndex: 3,
        borderRadius: '0px',
        overflow: 'hidden'
      };
    }

    // Check for percentage data using the EXACT property names from API
    const hasPercentageData = margins.xPercentage && margins.yPercentage && margins.widthPercentage && margins.heightPercentage &&
      margins.xPercentage !== "" && margins.yPercentage !== "" && margins.widthPercentage !== "" && margins.heightPercentage !== "";
    
    console.log('ContentSlide: ✅ EXACT API property check:', {
      hasMargins: !!margins,
      marginKeys: Object.keys(margins),
      xPercentage: margins.xPercentage,
      yPercentage: margins.yPercentage,
      widthPercentage: margins.widthPercentage,
      heightPercentage: margins.heightPercentage,
      hasPercentageData
    });
    
    if (!hasPercentageData) {
      console.log('❌ ContentSlide: Video margins missing percentage data for language:', currentLanguage);
      console.log('❌ ContentSlide: Expected: xPercentage, yPercentage, widthPercentage, heightPercentage');
      console.log('❌ ContentSlide: Available keys:', Object.keys(margins));
      
      // FALLBACK: Use default positioning
      console.log('ContentSlide: Using fallback positioning...');
      return {
        position: 'absolute',
        left: '10%',
        top: '10%',
        width: '30%',
        height: '30%',
        objectFit: 'cover',
        zIndex: 3,
        borderRadius: '0px',
        overflow: 'hidden'
      };
    }

    // Use the EXACT property names from API
    const videoXPercent = parseFloat(margins.xPercentage);
    const videoYPercent = parseFloat(margins.yPercentage);
    const videoWidthPercent = parseFloat(margins.widthPercentage);
    const videoHeightPercent = parseFloat(margins.heightPercentage);
    
    console.log('ContentSlide: Using percentage values from API:', {
      videoXPercent, videoYPercent, videoWidthPercent, videoHeightPercent
    });

    // Calculate actual image boundaries within the container
    // The image uses object-fit: contain, so we need to find the actual image display area
    let imageAreaLeft = 0;
    let imageAreaTop = 0;
    let imageAreaWidth = 100;
    let imageAreaHeight = 100;

    if (imageRef.current) {
      const container = imageRef.current.parentElement;
      const containerRect = container.getBoundingClientRect();
      
      // For object-fit: contain, calculate the actual displayed image area
      const containerAspectRatio = containerRect.width / containerRect.height;
      const imageAspectRatio = 3000 / 1688; // Slide aspect ratio
      
      if (imageAspectRatio > containerAspectRatio) {
        // Image is limited by width (letterboxing on top/bottom)
        const displayedHeight = containerRect.width / imageAspectRatio;
        const verticalOffset = (containerRect.height - displayedHeight) / 2;
        
        imageAreaLeft = 0;
        imageAreaTop = (verticalOffset / containerRect.height) * 100;
        imageAreaWidth = 100;
        imageAreaHeight = (displayedHeight / containerRect.height) * 100;
      } else {
        // Image is limited by height (letterboxing on left/right)
        const displayedWidth = containerRect.height * imageAspectRatio;
        const horizontalOffset = (containerRect.width - displayedWidth) / 2;
        
        imageAreaLeft = (horizontalOffset / containerRect.width) * 100;
        imageAreaTop = 0;
        imageAreaWidth = (displayedWidth / containerRect.width) * 100;
        imageAreaHeight = 100;
      }
      
      console.log('ContentSlide: Calculated image display area:', {
        containerSize: { width: containerRect.width, height: containerRect.height },
        containerAspectRatio,
        imageAspectRatio,
        imageArea: { left: imageAreaLeft, top: imageAreaTop, width: imageAreaWidth, height: imageAreaHeight }
      });
    }

    // Adjust video position to be relative to the actual image display area
    const adjustedX = imageAreaLeft + (videoXPercent * imageAreaWidth / 100);
    const adjustedY = imageAreaTop + (videoYPercent * imageAreaHeight / 100);
    const adjustedWidth = (videoWidthPercent * imageAreaWidth / 100);
    const adjustedHeight = (videoHeightPercent * imageAreaHeight / 100);

    // Ensure video stays within image boundaries by clamping values
    const clampedX = Math.max(imageAreaLeft, Math.min(adjustedX, imageAreaLeft + imageAreaWidth - adjustedWidth));
    const clampedY = Math.max(imageAreaTop, Math.min(adjustedY, imageAreaTop + imageAreaHeight - adjustedHeight));
    const clampedWidth = Math.min(adjustedWidth, imageAreaLeft + imageAreaWidth - clampedX);
    const clampedHeight = Math.min(adjustedHeight, imageAreaTop + imageAreaHeight - clampedY);

    // Position relative to the container but constrained within actual image display area
    const finalPosition = {
      position: 'absolute',
      left: `${clampedX}%`,
      top: `${clampedY}%`,
      width: `${clampedWidth}%`,
      height: `${clampedHeight}%`,
      objectFit: 'cover',
      zIndex: 3,
      borderRadius: '0px',
      overflow: 'hidden'
    };
    
    console.log('ContentSlide: ✅ Final video position calculated (image-area-constrained):', finalPosition);
    return finalPosition;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide.VideoMargins, currentLanguage, imageLoaded]); // Added imageLoaded back since we need image dimensions for boundary calculation

  // Update video position when slide or language changes
  // IMPORTANT: Calculate position IMMEDIATELY so video appears with slide (no delay)
  useEffect(() => {
    // Reset position stability on slide/language change
    setIsPositionStable(false);
    setIsCriticalUpdate(true);
    
    // Calculate position immediately (will use fallback if image not loaded)
    const updateVideoPosition = () => {
      console.log('ContentSlide: Updating video position (slide/language change)...');
      const newPosition = calculateVideoPosition();
      console.log('ContentSlide: New video position calculated:', newPosition);
      if (newPosition) {
        setVideoPosition(newPosition);
      }
    };

    // Calculate immediately - no delays
    updateVideoPosition();
    
    // Also recalculate after image loads to refine position (if using fallback)
    // Disable transitions during this critical update
    if (!imageLoaded) {
      const checkImageLoaded = setInterval(() => {
        if (imageLoaded || imageRef.current?.complete) {
          clearInterval(checkImageLoaded);
          // Refine position after image loads without transitions
          setIsCriticalUpdate(true);
          updateVideoPosition();
          // Enable transitions after a short delay
          setTimeout(() => {
            setIsCriticalUpdate(false);
            setIsPositionStable(true);
          }, 100);
        }
      }, 50);
      
      // Clear after 2 seconds max
      setTimeout(() => {
        clearInterval(checkImageLoaded);
        setIsCriticalUpdate(false);
        setIsPositionStable(true);
      }, 2000);
    } else {
      // Image already loaded, enable transitions after a short delay
      setTimeout(() => {
        setIsCriticalUpdate(false);
        setIsPositionStable(true);
      }, 100);
    }
  }, [calculateVideoPosition, slide.SlideNumber, slide.slideNumber, currentLanguage, imageLoaded]);

  // Debounced function to update video position without reloading video
  const updateVideoPositionSafely = useCallback(() => {
    // Clear any pending updates
    if (positionUpdateTimeoutRef.current) {
      clearTimeout(positionUpdateTimeoutRef.current);
    }
    
    // Save current video playback state before updating position
    if (videoRef.current && videoInitializedRef.current) {
      const video = videoRef.current;
      videoStateRef.current = {
        currentTime: video.currentTime,
        wasPlaying: !video.paused && !video.ended && video.readyState >= 2
      };
    }
    
    // Mark as critical update to disable transitions during bar visibility changes
    setIsCriticalUpdate(true);
    
    // Debounce position updates to prevent excessive recalculations
    positionUpdateTimeoutRef.current = setTimeout(() => {
      const newPosition = calculateVideoPosition();
      
      // Only update if position actually changed (to prevent unnecessary re-renders)
      // Compare percentage strings, not pixel values
      setVideoPosition(prevPosition => {
        if (!prevPosition || !newPosition) {
          return newPosition;
        }
        
        // Extract percentage values from strings like "10%"
        const extractPercent = (value) => {
          if (typeof value === 'string' && value.includes('%')) {
            return parseFloat(value);
          }
          return 0;
        };
        
        const prevLeft = extractPercent(prevPosition.left);
        const prevTop = extractPercent(prevPosition.top);
        const prevWidth = extractPercent(prevPosition.width);
        const prevHeight = extractPercent(prevPosition.height);
        
        const newLeft = extractPercent(newPosition.left);
        const newTop = extractPercent(newPosition.top);
        const newWidth = extractPercent(newPosition.width);
        const newHeight = extractPercent(newPosition.height);
        
        // Only update if position changed by more than 0.1% (to avoid tiny updates)
        if (Math.abs(prevLeft - newLeft) > 0.1 ||
            Math.abs(prevTop - newTop) > 0.1 ||
            Math.abs(prevWidth - newWidth) > 0.1 ||
            Math.abs(prevHeight - newHeight) > 0.1) {
          return newPosition;
        }
        return prevPosition; // No significant change, keep previous position
      });
      
      // Re-enable transitions after update completes
      setTimeout(() => {
        setIsCriticalUpdate(false);
      }, 50);
      
      // Restore video playback state after position update
      setTimeout(() => {
        if (videoRef.current && videoStateRef.current && videoInitializedRef.current) {
          const video = videoRef.current;
          const { currentTime, wasPlaying } = videoStateRef.current;
          
          // Only restore if video is ready
          if (video.readyState >= 2) {
            // Restore playback time (only if significantly different)
            if (currentTime > 0 && !isNaN(currentTime) && Math.abs(video.currentTime - currentTime) > 0.5) {
              video.currentTime = currentTime;
            }
            
            // Restore playing state if it was playing
            if (wasPlaying && video.paused && !video.ended) {
              video.play().catch(() => {});
            }
          }
        }
      }, 50);
    }, 150); // Debounce delay
  }, [calculateVideoPosition]);

  // Add window resize listener to update video position
  // IMPORTANT: Preserve video playback state during resize
  useEffect(() => {
    const handleResize = () => {
      updateVideoPositionSafely();
    };

    // Also add ResizeObserver for more precise container size tracking
    let resizeObserver;
    if (imageRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateVideoPositionSafely();
      });
      resizeObserver.observe(imageRef.current.parentElement);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (positionUpdateTimeoutRef.current) {
        clearTimeout(positionUpdateTimeoutRef.current);
      }
    };
  }, [updateVideoPositionSafely, imageLoaded]);

  // Add observer for bar visibility changes to update video position
  // IMPORTANT: Use debounced safe update to prevent video reload
  useEffect(() => {
    const handleBarVisibilityChange = () => {
      // Use the safe update function which debounces and preserves video state
      updateVideoPositionSafely();
    };

    // Watch for class changes on app element that indicate bar visibility
    const appElement = document.querySelector('.app');
    if (appElement) {
      const observer = new MutationObserver(handleBarVisibilityChange);
      observer.observe(appElement, { attributes: true, attributeFilter: ['class'] });
      
      return () => {
        observer.disconnect();
        if (positionUpdateTimeoutRef.current) {
          clearTimeout(positionUpdateTimeoutRef.current);
    }
      };
    }
  }, [updateVideoPositionSafely]);

  // Track imageLoaded state changes
  useEffect(() => {
    console.log('ContentSlide: imageLoaded state changed to:', imageLoaded);
  }, [imageLoaded]);

  // Track video element mounting and language changes
  useEffect(() => {
    if (videoRef.current) {
      console.log('ContentSlide: Video element mounted/updated:', videoRef.current);
      console.log('ContentSlide: Video src:', videoRef.current.src);
      console.log('ContentSlide: Video sources:', Array.from(videoRef.current.children).map(child => child.src));
      
      // Ensure video starts playing when mounted/updated
      const playVideo = () => {
        if (videoRef.current && videoRef.current.readyState >= 2) { // HAVE_CURRENT_DATA
          console.log('ContentSlide: Video ready, attempting to play');
          videoRef.current.play().catch(error => {
            console.error('ContentSlide: Video play failed on mount:', error);
          });
        } else {
          console.log('ContentSlide: Video not ready yet, waiting...');
        }
      };
      
      // Try to play immediately if ready, otherwise wait for loadeddata event
      playVideo();
    }
  }, [videoPosition, currentLanguage]); // Re-run when videoPosition or language changes

  // Handle language changes - more aggressive approach
  useEffect(() => {
    console.log('=== LANGUAGE CHANGE DETECTED ===');
    console.log('ContentSlide: Language changed from previous to:', currentLanguage);
    
    // Check if the new language should have a video
    const shouldShow = shouldShowVideo();
    console.log('ContentSlide: Should show video for', currentLanguage + ':', shouldShow);
    
    // Only log if the new language should have a video
    if (shouldShow) {
      console.log('ContentSlide: Language has valid video, will render video element');
    } else {
      console.log('ContentSlide: Language has no valid video, will not render video element');
    }
    
    // Reset states
    setVideoPosition(null);
    
    // Recalculate position
    const newPosition = calculateVideoPosition();
    console.log('ContentSlide: New position calculated for', currentLanguage + ':', newPosition);
    setVideoPosition(newPosition);
    
    // Force video to restart after a delay to ensure DOM is updated
    setTimeout(() => {
      if (videoRef.current) {
        console.log('ContentSlide: FORCING VIDEO RESTART for language:', currentLanguage);
        console.log('ContentSlide: Video element exists:', !!videoRef.current);
        console.log('ContentSlide: Video src before load:', videoRef.current.currentSrc);
        
        // Let the video load naturally without forcing reload
        
        // Multiple attempts to ensure playback
        const forcePlay = async () => {
          try {
            console.log('ContentSlide: Attempting to play video...');
            await videoRef.current.play();
            console.log('ContentSlide: ✅ Video playing successfully for:', currentLanguage);
          } catch (error) {
            console.log('ContentSlide: ❌ Video autoplay failed:', error.message);
          }
        };
        
        // Try immediately and also after loadeddata
        forcePlay();
        
        // Add event listener for when video is ready (guard against unmounts)
        const videoEl = videoRef.current;
        if (videoEl) {
          const onLoadedData = () => {
            console.log('ContentSlide: Video data loaded, attempting play again');
            forcePlay();
            // Use captured element reference; videoRef.current may be null after navigation
            try {
              videoEl.removeEventListener('loadeddata', onLoadedData);
            } catch (e) {
              // Element might already be detached; ignore
            }
          };
          videoEl.addEventListener('loadeddata', onLoadedData);
        }
      } else {
        console.log('ContentSlide: ❌ Video element not found after language change');
      }
    }, 300);
    
  }, [currentLanguage, calculateVideoPosition, shouldShowVideo]);
  
  // Separate effect to handle video element when position changes
  useEffect(() => {
    if (videoPosition && videoRef.current) {
      console.log('ContentSlide: Video position updated, reloading video element');
      
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (videoRef.current) {
          console.log('ContentSlide: Video ready for language:', currentLanguage);
          // Video will load automatically when src is set
          
          // Attempt to play after load
          const attemptPlay = () => {
            if (videoRef.current && videoRef.current.readyState >= 2) {
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  console.log('ContentSlide: Video playing successfully');
                }).catch(error => {
                  console.log('ContentSlide: Autoplay prevented:', error.message);
                });
              }
            }
          };
          
          // Try to play immediately if ready, otherwise wait for canplay event
          attemptPlay();
        }
      }, 100);
    }
  }, [videoPosition, currentLanguage]);

  // Handle image load
  const handleImageLoad = () => {
    console.log('ContentSlide: Image loaded successfully');
    
    // Clear loading flag
    imageLoadingRef.current = false;
    
    // Clear any pending error timeout since image loaded successfully
    if (imageErrorTimeoutRef.current) {
      clearTimeout(imageErrorTimeoutRef.current);
      imageErrorTimeoutRef.current = null;
    }
    
    // Ensure image is visible
    if (imageRef.current) {
      imageRef.current.style.display = '';
    }
    
    // Hide fallback content when image loads successfully
    if (imageRef.current?.parentElement) {
      const fallbackDiv = imageRef.current.parentElement.querySelector('.slide-fallback-content');
      if (fallbackDiv) {
        fallbackDiv.style.display = 'none';
      }
    }
    
    // Small delay to ensure image is fully positioned
    setTimeout(() => {
      setImageLoaded(true);
      console.log('ContentSlide: imageLoaded state set to true');
      
      // Recalculate video position with accurate image dimensions
      // Disable transitions during this update
      setIsCriticalUpdate(true);
      const newPosition = calculateVideoPosition();
      if (newPosition) {
        setVideoPosition(newPosition);
      }
      
      // Re-enable transitions after position update
      setTimeout(() => {
        setIsCriticalUpdate(false);
        setIsPositionStable(true);
      }, 50);
    }, 100);
  };

  // Get the appropriate image for current language
  const getSlideImage = () => {
    const langCode = currentLanguage || 'en';
    const langCodeUpper = langCode.charAt(0).toUpperCase() + langCode.slice(1);
    
    console.log('ContentSlide: Getting image for language:', langCode, langCodeUpper);
    console.log('ContentSlide: Slide data:', slide);
    
    // Try both uppercase and lowercase property names
    const images = slide.Images || slide.images;
    
    console.log('ContentSlide: Available images:', images);
    
    if (images) {
      // Try both language code formats
      const imageUrl = images[langCode] || images[langCodeUpper] || images['en'] || images['En'] || slide.imageUrl;
      console.log('ContentSlide: Selected image URL:', imageUrl);
      if (imageUrl) {
        return imageUrl;
      }
    }
    
    // Only use slide.imageUrl if available (from blob storage)
    // No local fallback - all assets come from blob storage
    console.log('ContentSlide: No image available in Images object, using slide.imageUrl:', slide.imageUrl);
    return slide.imageUrl || null;
  };

  // No text processing needed for content slides

  // Handle click on slide to ensure video continues playing
  const handleSlideClick = () => {
    console.log('ContentSlide: Slide clicked, ensuring video plays');
    
    if (videoRef.current) {
      const video = videoRef.current;
      
      // Ensure video is playing if it's paused
      if (video.paused) {
        // Ensure proper configuration
        video.muted = true;
        video.volume = 0;
        video.playsInline = true;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        
        video.play().catch(error => {
          console.log('ContentSlide: Click play failed:', error.message);
          });
      }
    }
  };

  return (
    <div className="content-slide" ref={slideContainerRef} onClick={handleSlideClick}>
      {/* Image container */}
      <div className="slide-image-container">
        <img
          ref={imageRef}
          key={`slide-${slide.slideNumber || slide.SlideNumber}-${currentLanguage}`}
          src={getSlideImage()}
          alt="Slide content"
          className="slide-background-image"
          onLoadStart={() => {
            // Set loading flag when image starts loading
            imageLoadingRef.current = true;
            console.log('ContentSlide: Image load started');
          }}
          onLoad={handleImageLoad}
          onError={(e) => {
            const errorTarget = e.target;
            const errorSrc = errorTarget.src;
            
            console.error('ContentSlide: Image error event triggered:', errorSrc);
            console.error('ContentSlide: Image complete:', errorTarget.complete);
            console.error('ContentSlide: Image naturalWidth:', errorTarget.naturalWidth);
            console.error('ContentSlide: Image naturalHeight:', errorTarget.naturalHeight);
            console.error('ContentSlide: Image loading flag:', imageLoadingRef.current);
            
            // Don't show fallback if src is empty (during reset)
            if (!errorSrc || errorSrc === '' || errorSrc === window.location.href) {
              console.log('ContentSlide: Image src is empty or invalid (during reset), ignoring error');
              return;
            }
            
            // Don't show fallback if image is currently loading
            if (imageLoadingRef.current) {
              console.log('ContentSlide: Image is currently loading, ignoring error event');
              return;
            }
            
            // Check if image actually failed or is just loading
            // If image has dimensions, it might have loaded successfully
            if (errorTarget.naturalWidth > 0 && errorTarget.naturalHeight > 0) {
              console.log('ContentSlide: Image has dimensions, might have loaded successfully, not showing fallback');
              return;
            }
            
            // Clear any existing timeout
            if (imageErrorTimeoutRef.current) {
              clearTimeout(imageErrorTimeoutRef.current);
            }
            
            // Wait longer and verify the image actually failed before showing fallback
            // This prevents false positives when image is still loading
            imageErrorTimeoutRef.current = setTimeout(() => {
              // Double-check the image state before showing fallback
              if (errorTarget && errorTarget.src === errorSrc && !imageLoadingRef.current) {
                // Check if image actually failed (no dimensions and not complete)
                const hasFailed = !errorTarget.complete && 
                                  errorTarget.naturalWidth === 0 && 
                                  errorTarget.naturalHeight === 0 &&
                                  !imageLoaded;
                
                if (hasFailed) {
                  console.warn('ContentSlide: Blob storage image confirmed failed, showing text fallback');
                  errorTarget.style.display = 'none';
                  const fallbackDiv = errorTarget.parentElement?.querySelector('.slide-fallback-content');
                  if (fallbackDiv) {
                    fallbackDiv.style.display = 'flex';
                  }
                } else {
                  console.log('ContentSlide: Image appears to be loading or loaded, not showing fallback');
                }
              }
              imageErrorTimeoutRef.current = null;
            }, 2000); // Increased timeout to 2 seconds to allow image to load
          }}
        />
        
        {/* Fallback content when image fails to load */}
        <div className="slide-fallback-content" style={{
          display: 'none',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: '#fff',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div style={{
            fontSize: '1.5rem',
            marginBottom: '1rem',
            opacity: 0.8
          }}>
            📖 Slide Content
          </div>
          <div style={{
            fontSize: '1rem',
            opacity: 0.6,
            maxWidth: '600px',
            lineHeight: '1.5'
          }}>
            {slide.text?.[currentLanguage] || slide.Text?.[currentLanguage] || 
             slide.text?.en || slide.Text?.En || 
             'Slide content is loading...'}
          </div>
        </div>
        
        {/* Video Container - always render but hide until positioned to prevent remounting */}
        {(() => {
          const showVideo = shouldShowVideo();
          const hasPosition = !!videoPosition;
          const willRender = showVideo && hasPosition;
          const slideNumber = slide.SlideNumber || slide.slideNumber;
          
          console.log('ContentSlide: Video render check for language', currentLanguage + ':', {
            shouldShowVideo: showVideo,
            hasVideoPosition: hasPosition,
            videoPosition,
            imageLoaded,
            willRenderVideo: willRender
          });
          
          // Always render video container to prevent remounting
          // Use calculated position or fallback - show immediately with slide
          const displayPosition = videoPosition || {
            position: 'absolute',
            left: '0%',
            top: '0%',
            width: '0%',
            height: '0%',
            opacity: 0
          };
          
          return (
            <div 
              className="video-container video-custom"
              style={{
                ...displayPosition,
                opacity: willRender ? 1 : 0,
                visibility: willRender ? 'visible' : 'hidden',
                pointerEvents: willRender ? 'auto' : 'none',
                // Force GPU acceleration to prevent video reload on resize
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
                // Disable transitions during critical updates (initial load, bar visibility changes)
                transition: (isPositionStable && !isCriticalUpdate) 
                  ? 'left 0.3s ease, top 0.3s ease, width 0.3s ease, height 0.3s ease, opacity 0.2s ease'
                  : 'none'
              }}
            >
          <video
           ref={videoRef}
               key={`video-${slideNumber}-${currentLanguage}`}
           controls={false}
           controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
           disablePictureInPicture={true}
           autoPlay={false}
           loop={true}
           muted={true}
           playsInline={true}
           webkit-playsinline="true"
           x-webkit-airplay="allow"
           data-force-autoplay="true"
            className="slide-video"
           preload="metadata"
           onContextMenu={(e) => e.preventDefault()}
          onLoadStart={() => {
            // Only log if video is actually being loaded (not on re-renders)
            if (!videoInitializedRef.current) {
              console.log('ContentSlide: Video load started for:', currentLanguage);
            }
          }}
           onLoadedData={() => {
            // Only log if video is actually being loaded (not on re-renders)
            if (!videoInitializedRef.current) {
             console.log('ContentSlide: Video data loaded for language:', currentLanguage);
            }
           }}
           onCanPlay={() => {
             console.log('ContentSlide: Video can play for language:', currentLanguage, 'isIOS:', isIOS);
             
            // Only force autoplay if video hasn't been initialized yet
            // This prevents restart when bars hide/show cause re-renders
            if (videoRef.current && !videoInitializedRef.current) {
               const video = videoRef.current;
               
               // Ensure maximum compatibility settings
               video.muted = true;
               video.volume = 0;
               video.playsInline = true;
               video.setAttribute('playsinline', 'true');
               video.setAttribute('webkit-playsinline', 'true');
               video.setAttribute('x-webkit-airplay', 'allow');
               
               // Force play without any conditions
               const forcePlay = async () => {
                 try {
                   await video.play();
                   console.log('ContentSlide: Force autoplay successful on canPlay');
                  videoInitializedRef.current = true; // Mark as initialized
                 } catch (error) {
                   console.log('ContentSlide: Force autoplay failed on canPlay, trying alternative method');
                   // Try alternative method for iOS
                   video.currentTime = 0.1; // Tiny seek to trigger play
                   setTimeout(() => {
                     video.play().catch(() => {
                       console.log('ContentSlide: All play attempts failed, but video is ready');
                      videoInitializedRef.current = true; // Mark as initialized even if play failed
                     });
                   }, 100);
                 }
               };
               
               forcePlay();
            } else if (videoRef.current && videoInitializedRef.current) {
              // Video already initialized, just ensure it's playing if it was playing before
              const video = videoRef.current;
              if (videoStateRef.current.wasPlaying && video.paused) {
                video.play().catch(() => {});
              }
             }
           }}
           onPlay={() => {
             if (!videoInitializedRef.current) {
             console.log('ContentSlide: Video started playing for language:', currentLanguage);
               videoInitializedRef.current = true;
             }
           }}
           onPause={() => {
             // Don't auto-resume if video is intentionally paused or ended
             // Only resume if it was playing and got paused unexpectedly during position update
             if (videoRef.current && !videoRef.current.ended && videoStateRef.current.wasPlaying) {
               // Small delay to avoid rapid pause/play cycles
               setTimeout(() => {
                 if (videoRef.current && videoRef.current.paused && !videoRef.current.ended) {
                   videoRef.current.play().catch(error => {
                     console.log('ContentSlide: Auto-resume failed:', error);
                   });
                 }
               }, 100);
             }
           }}
           onEnded={() => {
             console.log('ContentSlide: Video ended for language:', currentLanguage);
             // Loop the video
             if (videoRef.current) {
               videoRef.current.currentTime = 0;
               videoRef.current.play();
             }
           }}
           onError={(e) => {
             console.warn('ContentSlide: Video loading issue for language:', currentLanguage, {
               errorCode: e.target.error?.code,
               errorMessage: e.target.error?.message,
               currentSrc: e.target.currentSrc,
               srcAttribute: e.target.src,
               readyState: e.target.readyState,
               networkState: e.target.networkState
             });
             
             // For network errors (often CORS-related), still try to show video
             if (e.target.networkState === 3 || e.target.error?.code === 2) {
               console.log('ContentSlide: Network/format error, but continuing with video display');
             }
           }}
           onLoadedMetadata={() => {
             console.log('ContentSlide: Video metadata loaded for:', currentLanguage);
           }}
           style={{
             outline: 'none',
             border: 'none'
           }}
         >
           {(() => {
             const videoSrc = getVideoFiles();
             console.log('ContentSlide: Rendering video with src:', videoSrc);
             console.log('ContentSlide: Video src type:', typeof videoSrc);
             console.log('ContentSlide: Video src length:', videoSrc ? videoSrc.length : 'null');
             
             if (!videoSrc) {
               console.error('ContentSlide: No video source available!');
               return <>{t('ui.content.videoNotSupported')}</>;
             }
             
             // Simple source element without CORS restrictions
             console.log('ContentSlide: Rendering video sources for:', videoSrc);
             
             // Try different video formats and fallbacks
             if (videoSrc.includes('.webm')) {
               return (
                 <>
                   <source src={videoSrc} type="video/webm" />
                   <source src={videoSrc.replace('.webm', '.mp4')} type="video/mp4" />
                   {t('ui.content.videoNotSupported')}
                 </>
               );
             } else {
               return (
                 <>
                   <source src={videoSrc} type="video/mp4" />
                   <source src={videoSrc.replace('.mp4', '.webm')} type="video/webm" />
                   {t('ui.content.videoNotSupported')}
                 </>
               );
             }
           })()}
          </video>
        </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ContentSlide; 